import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { FaRegCheckCircle, FaRegTimesCircle, FaLock, FaExchangeAlt } from "react-icons/fa";
import { useToast } from '../components/ui/ToastContext';
import { API_URL } from '../config.js';

function AuthPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [twoFactorToken, setTwoFactorToken] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const navigate = useNavigate();
  const { showToast } = useToast();

  const handleSuccess = () => {
    showToast({
      message: "Login cu succes!",
      type: "success",
      icon: <FaRegCheckCircle className="text-cyan-600 dark:text-cyan-500" />,
    });
  };

  const handleError = () => {
    showToast({
      message: "Nume de utilizator sau parolă incorectă!",
      type: "error",
      icon: <FaRegTimesCircle className="text-cyan-600 dark:text-cyan-500" />,
    });
  };

  const handleBlock = () => {
    showToast({
      message: "Cont blocat! Anunțați administratorul!",
      type: "error",
      icon: <FaLock className="text-red-600 dark:text-red-500" />,
    });
  };

  const handleMustChangePassword = () => {
    showToast({
      message: "Este nevoie să vă schimbați parola inițială înainte de a continua!",
      type: "info",
      icon: <FaExchangeAlt className="text-cyan-600 dark:text-cyan-500" />,
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const response = await axios.post(
        `${API_URL}/api/login`,
        { username: username.trim(), password },
        { withCredentials: true }
      );

      const type = response.data;
      if (response.status === 200) {
        if (response.data.requiresTwoFactor) {
          setTwoFactorToken(response.data.twoFactorToken);
          setTwoFactorCode('');
          return;
        }
        const { accessToken, refreshToken, isActive, is_active, mustChangePassword } = response.data;

        if (isActive === false || isActive === 0 || is_active === false || is_active === 0) {
          handleBlock();
          setIsLoading(false);
          return;
        }

        if (mustChangePassword === 1 || mustChangePassword === true) {
          handleMustChangePassword();
          navigate("/forgot-password", { state: { username: username.trim() } });
          return;
        } else {
          if (type.user?.role === "Admin") {
            navigate("/users");
          } else if (type.user?.role === "Marketing monitor") {
            navigate("/marketing/consent");
          } else {
            navigate("/dashboard");
          }
          localStorage.setItem("jwtToken", accessToken);
          localStorage.setItem("refreshToken", refreshToken);
          document.cookie = `accessToken=${accessToken}; path=/; Secure; HttpOnly`;
          handleSuccess();
        }
      }
    } catch (err) {
      console.error("Login error:", err.response?.data || err.message);

      if (err.response) {
        const errorMsg = err.response.data?.error || "";

        if (errorMsg === "Must change password." || err.response.data?.mustChangePassword) {
          handleMustChangePassword();
          navigate("/forgot-password", { state: { username: username.trim() } });
        } else if (errorMsg.toLowerCase().includes("blocat") || err.response.status === 403) {
          handleBlock();
        } else if (errorMsg === "Invalid username or password." || err.response.status === 401) {
          handleError();
        } else {
          showToast({
            message: errorMsg || "A apărut o eroare la autentificare.",
            type: "error",
            icon: <FaRegTimesCircle className="text-red-600 dark:text-red-500" />,
          });
        }
      } else {
        showToast({
          message: "Nu s-a putut contacta serverul.",
          type: "error",
          icon: <FaRegTimesCircle className="text-red-600 dark:text-red-500" />,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleTwoFactorLogin = async (event) => {
    event.preventDefault();
    if (!/^\d{6}$/.test(twoFactorCode)) {
      setError('Introdu codul din 6 cifre afișat în aplicație.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API_URL}/api/login/2fa`, {
        twoFactorToken,
        code: twoFactorCode,
      }, { withCredentials: true });
      const { accessToken, refreshToken, user } = response.data;
      localStorage.setItem('jwtToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      handleSuccess();
      if (user?.role === "Admin") {
        navigate("/users");
      } else if (user?.role === "Marketing monitor") {
        navigate("/marketing/consent");
      } else {
        navigate("/dashboard");
      }
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Codul nu a putut fi verificat.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex justify-center items-center h-screen bg-gray-100 dark:bg-gray-900">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg max-w-sm w-full">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-2">
          {twoFactorToken ? 'Verificare în doi pași' : 'Login'}
        </h2>
        {twoFactorToken && <p className="mb-5 text-sm leading-6 text-gray-600 dark:text-gray-300">Introdu codul temporar din aplicația ta de autentificare.</p>}
        <form onSubmit={twoFactorToken ? handleTwoFactorLogin : handleLogin}>
          {!twoFactorToken ? <>
          <div className="mb-4">
            <label
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              htmlFor="username"
            >
              Username
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username"
              className="mt-1 block w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>
          <div className="mb-4">
            <label
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              htmlFor="password"
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              className="mt-1 block w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>
          </> : (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="two-factor-code">Cod de autentificare</label>
              <input id="two-factor-code" autoFocus inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                value={twoFactorCode} onChange={(event) => setTwoFactorCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="mt-1 block w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-center font-mono text-lg tracking-[0.3em] text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100" />
            </div>
          )}
          {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full py-2 px-4 text-white font-semibold rounded-lg shadow-md ${isLoading ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'
              } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75`}
          >
            {isLoading ? 'Se verifică…' : twoFactorToken ? 'Verifică' : 'Connect'}
          </button>

          {/* Forgot Password Button */}
          <div className="mt-4 text-center">
            {twoFactorToken ? (
              <button type="button" onClick={() => { setTwoFactorToken(null); setTwoFactorCode(''); setError(''); }} className="text-sm text-blue-600 hover:text-blue-800 focus:outline-none">Înapoi la autentificare</button>
            ) : (
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className="text-sm text-blue-600 hover:text-blue-800 focus:outline-none"
            >
              Forgot Password?
            </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default AuthPage;
